"use client";

import { type CreateRuleInput, CreateRuleSchema } from "@gosaas/schemas";
import type {
	ConstantsNotificationRuleResponse,
	ConstantsPublicTeamMemberResponse,
} from "@gosaas/types";
import {
	PREDEFINED_NOTIFICATION_CHANNEL_SHORT_LABELS,
	PREDEFINED_NOTIFICATION_CHANNELS,
	USAGE_THRESHOLD_PERCENTS_ALLOWED,
} from "@gosaas/types/notification";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconPencil, IconPlus, IconTrash } from "@tabler/icons-react";
import * as React from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { toast } from "sonner";
import { useTeam } from "@/components/providers/team";
import {
	useFetchNotificationChannels,
	useFetchNotificationEvents,
} from "@/components/queries/notifications";
import { useFetchTeamMembers } from "@/components/queries/team";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { apiClient, requestWithHandling } from "@/utils/api";

const PREDEFINED_CHANNEL_OPTIONS = PREDEFINED_NOTIFICATION_CHANNELS.map(
	(value) => ({
		value,
		label: PREDEFINED_NOTIFICATION_CHANNEL_SHORT_LABELS[value],
	}),
);

export function formatEventLabel(event: string): string {
	return event
		.toLowerCase()
		.split("_")
		.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
		.join(" ");
}

export type NotificationEventGroup = {
	id: string;
	title: string;
	events: string[];
};

/** Partitions API event names into UI groups (prefix-based, case-insensitive). */
export function groupNotificationEventsByPrefix(
	events: string[] | undefined,
): NotificationEventGroup[] {
	if (!events?.length) return [];
	const subscription: string[] = [];
	const usage: string[] = [];
	const webhooks: string[] = [];
	const other: string[] = [];
	for (const ev of events) {
		const u = ev.toUpperCase();
		if (u.startsWith("SUBSCRIPTION_")) subscription.push(ev);
		else if (u.startsWith("USAGE_")) usage.push(ev);
		else if (u.startsWith("WEBHOOK_")) webhooks.push(ev);
		else other.push(ev);
	}
	const out: NotificationEventGroup[] = [];
	if (subscription.length > 0) {
		out.push({
			id: "subscription",
			title: "Subscription",
			events: subscription,
		});
	}
	if (usage.length > 0) {
		out.push({ id: "usage", title: "Usage", events: usage });
	}
	if (webhooks.length > 0) {
		out.push({ id: "webhooks", title: "Webhooks", events: webhooks });
	}
	if (other.length > 0) {
		out.push({ id: "other", title: "Other", events: other });
	}
	return out;
}

export function usageThresholdPercentsFromConditions(
	conditions: unknown,
): number[] {
	if (!conditions || typeof conditions !== "object") return [];
	const usage = (conditions as Record<string, unknown>).usage_threshold;
	if (!usage || typeof usage !== "object") return [];
	const percents = (usage as Record<string, unknown>).threshold_percents;
	return Array.isArray(percents)
		? percents.filter((p): p is number => typeof p === "number")
		: [];
}

export function hasUsageThresholdEvent(eventTypes: string[]): boolean {
	return eventTypes.some((ev) => ev.toUpperCase() === "USAGE_API_THRESHOLD");
}

export function formatMemberDisplayName(
	m: ConstantsPublicTeamMemberResponse,
): string {
	const parts = [m.first_name, m.last_name].filter(
		(s) => typeof s === "string" && s.trim().length > 0,
	);
	if (parts.length === 0) return m.email;
	return parts.join(" ");
}

export function teamMemberNavLabel(
	m: ConstantsPublicTeamMemberResponse,
): string {
	return `${formatMemberDisplayName(m)} ${m.email} ${m.role}`;
}

export function normalizeEventTypesFieldValue(value: unknown): string[] {
	return Array.isArray(value) ? value : [];
}

export function ruleToFormDefaults(
	rule: ConstantsNotificationRuleResponse,
): CreateRuleInput {
	const thresholdPercents = usageThresholdPercentsFromConditions(
		rule.conditions,
	);
	return {
		name: rule.name ?? "",
		description: rule.description ?? "",
		event_types: rule.event_types?.length ? rule.event_types : [],
		conditions:
			thresholdPercents.length > 0
				? {
						usage_threshold: {
							feature_key: "api_requests",
							threshold_percents: thresholdPercents,
						},
					}
				: undefined,
		is_enabled: rule.is_enabled ?? true,
		channels:
			rule.channels?.map((ch) => ({
				channel_id: ch.channel_id ?? undefined,
				predefined: ch.predefined ?? undefined,
				target_user_id: ch.target_user_id ?? undefined,
			})) ?? [],
	};
}

const emptyDefaults: CreateRuleInput = {
	name: "",
	description: "",
	event_types: [],
	conditions: undefined,
	is_enabled: true,
	channels: [],
};

interface NotificationRuleDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess?: () => void;
	/** When set, dialog updates this rule (PUT); otherwise creates (POST). */
	editingRule?: ConstantsNotificationRuleResponse | null;
}

export function CreateNotificationRuleDialog({
	open,
	onOpenChange,
	onSuccess,
	editingRule = null,
}: NotificationRuleDialogProps) {
	const [isLoading, setIsLoading] = React.useState(false);
	const { teamID } = useTeam();
	const prefetchDeps = !!teamID;

	const { events, isLoadingEvents } = useFetchNotificationEvents(
		teamID ?? "",
		prefetchDeps,
	);
	const { channels } = useFetchNotificationChannels(
		teamID ?? "",
		open && prefetchDeps,
	);
	const { teamMembers } = useFetchTeamMembers(
		teamID ?? "",
		open && prefetchDeps,
	);

	const verifiedChannels = channels?.filter((c) => c.is_verified) ?? [];

	const form = useForm<CreateRuleInput>({
		resolver: zodResolver(CreateRuleSchema),
		defaultValues: emptyDefaults,
	});

	const { fields, append, remove } = useFieldArray({
		control: form.control,
		name: "channels",
	});

	React.useEffect(() => {
		if (!open) return;
		if (editingRule) {
			form.reset(ruleToFormDefaults(editingRule));
		} else {
			form.reset(emptyDefaults);
		}
	}, [open, editingRule, form]);

	function addTarget(value: string) {
		if (value.startsWith("predefined:")) {
			const predefined = value.slice("predefined:".length);
			if (predefined === "notify_user") {
				append({ predefined: "notify_user", target_user_id: "" });
			} else {
				append({ predefined });
			}
		} else if (value.startsWith("channel:")) {
			const channelId = value.slice("channel:".length);
			append({ channel_id: channelId });
		}
	}

	async function onSubmit(data: CreateRuleInput) {
		try {
			setIsLoading(true);
			const payloadChannels = data.channels.map((ch) => ({
				channel_id: ch.channel_id,
				predefined: ch.predefined,
				target_user_id: ch.target_user_id,
			}));
			const payloadConditions = hasUsageThresholdEvent(data.event_types)
				? data.conditions
				: undefined;

			if (editingRule?.id) {
				const response = await requestWithHandling<void>(() =>
					apiClient.api.teamNotificationsRulesUpdate(
						teamID as string,
						editingRule.id as string,
						{
							name: data.name,
							description: data.description,
							event_types: data.event_types,
							conditions: payloadConditions,
							is_enabled: data.is_enabled,
							channels: payloadChannels,
						},
					),
				);
				if (response.code !== "SUCCESS") {
					toast.error("Failed to update rule", {
						description: response.message,
					});
					return;
				}
				toast.success("Notification rule updated.");
			} else {
				const response = await requestWithHandling<void>(() =>
					apiClient.api.teamNotificationsRulesCreate(teamID as string, {
						name: data.name,
						description: data.description,
						event_types: data.event_types,
						conditions: payloadConditions,
						is_enabled: data.is_enabled,
						channels: payloadChannels,
					}),
				);
				if (response.code !== "SUCCESS") {
					toast.error("Failed to create rule", {
						description: response.message,
					});
					return;
				}
				toast.success("Notification rule created.");
			}
			form.reset(emptyDefaults);
			onOpenChange(false);
			onSuccess?.();
		} catch {
			toast.error("Couldn't save this rule", {
				description: "Try again or reload the page.",
			});
		} finally {
			setIsLoading(false);
		}
	}

	const isEdit = Boolean(editingRule?.id);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						{isEdit ? (
							<IconPencil className="h-5 w-5" />
						) : (
							<IconPlus className="h-5 w-5" />
						)}
						{isEdit ? "Edit notification rule" : "Create notification rule"}
					</DialogTitle>
					<DialogDescription>
						Choose which events trigger this rule and where to deliver
						notifications.
					</DialogDescription>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Rule name</FormLabel>
									<FormControl>
										<Input placeholder="e.g. Billing alerts" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="description"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Description</FormLabel>
									<FormControl>
										<Input placeholder="Optional description" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="event_types"
							render={({ field }) => {
								const groups = groupNotificationEventsByPrefix(events);
								return (
									<FormItem>
										<FormLabel>Events</FormLabel>
										<fieldset className="space-y-3">
											<legend className="sr-only">
												Select one or more notification event types
											</legend>
											{isLoadingEvents ? (
												<div className="rounded-md border p-3 text-sm text-muted-foreground">
													Loading events…
												</div>
											) : groups.length === 0 ? (
												<div className="rounded-md border p-3 text-sm text-muted-foreground">
													No events available.
												</div>
											) : (
												groups.map((group) => (
													<div
														key={group.id}
														className="rounded-md border p-3 space-y-2"
													>
														<p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
															{group.title}
														</p>
														<div className="space-y-2">
															{group.events.map((ev) => {
																const eventFieldId = `notification-rule-event-${ev}`;
																const selectedEvents =
																	normalizeEventTypesFieldValue(field.value);
																return (
																	<label
																		key={ev}
																		htmlFor={eventFieldId}
																		className="flex cursor-pointer items-center gap-2 text-sm"
																	>
																		<Checkbox
																			id={eventFieldId}
																			checked={selectedEvents.includes(ev)}
																			onCheckedChange={(checked) => {
																				const next = new Set(selectedEvents);
																				if (checked) next.add(ev);
																				else next.delete(ev);
																				field.onChange([...next]);
																			}}
																		/>
																		<span>{formatEventLabel(ev)}</span>
																	</label>
																);
															})}
														</div>
													</div>
												))
											)}
										</fieldset>
										<FormMessage />
									</FormItem>
								);
							}}
						/>

						{hasUsageThresholdEvent(
							normalizeEventTypesFieldValue(form.watch("event_types")),
						) && (
							<FormField
								control={form.control}
								name="conditions.usage_threshold.threshold_percents"
								render={({ field }) => {
									const selected = Array.isArray(field.value)
										? field.value
										: [];
									return (
										<FormItem>
											<FormLabel>Alert when usage reaches</FormLabel>
											<div className="flex flex-wrap gap-2">
												{USAGE_THRESHOLD_PERCENTS_ALLOWED.map((pct) => {
													const checked = selected.includes(pct);
													const inputId = `usage-threshold-${pct}`;
													return (
														<div
															key={pct}
															className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm"
														>
															<Checkbox
																id={inputId}
																aria-label={`Alert at ${pct}% usage`}
																checked={checked}
																onCheckedChange={(next) => {
																	const set = new Set(selected);
																	if (next) set.add(pct);
																	else set.delete(pct);
																	field.onChange(
																		[...set].sort((a, b) => a - b),
																	);
																}}
															/>
															<label
																htmlFor={inputId}
																className="cursor-pointer"
															>
																{pct}%
															</label>
														</div>
													);
												})}
											</div>
											<FormMessage />
										</FormItem>
									);
								}}
							/>
						)}

						<div className="space-y-2">
							<FormLabel>Notify</FormLabel>
							<div className="space-y-2">
								{fields.map((field, index) => (
									<div key={field.id} className="flex items-center gap-2">
										<div className="flex-1 rounded-md border px-3 py-2.5 text-sm">
											{field.channel_id ? (
												<span>
													{verifiedChannels.find(
														(c) => c.id === field.channel_id,
													)?.name ?? field.channel_id}
												</span>
											) : field.predefined === "notify_user" ? (
												<div className="space-y-1.5">
													<p className="text-xs font-medium text-muted-foreground">
														{
															PREDEFINED_NOTIFICATION_CHANNEL_SHORT_LABELS.notify_user
														}
													</p>
													<Select
														onValueChange={(val) =>
															form.setValue(
																`channels.${index}.target_user_id`,
																val ?? undefined,
															)
														}
														value={
															form.watch(`channels.${index}.target_user_id`) ||
															undefined
														}
													>
														<SelectTrigger
															size="sm"
															className="h-9 w-full max-w-full border bg-background px-2.5 shadow-none"
														>
															<SelectValue placeholder="Select user">
																{(userId) => {
																	if (userId == null || userId === "") {
																		return null;
																	}
																	const m = teamMembers?.find(
																		(x) => x.user_id === userId,
																	);
																	return (
																		m?.email ?? (
																			<span className="text-muted-foreground">
																				Unknown user
																			</span>
																		)
																	);
																}}
															</SelectValue>
														</SelectTrigger>
														<SelectContent
															align="start"
															className="min-w-[min(calc(100vw-2rem),22rem)]"
														>
															{teamMembers?.map((m) => (
																<SelectItem
																	key={m.user_id}
																	value={m.user_id}
																	label={teamMemberNavLabel(m)}
																>
																	<div className="flex min-w-0 flex-col gap-0.5 py-0.5 text-left">
																		<span className="truncate font-medium leading-tight">
																			{formatMemberDisplayName(m)}
																		</span>
																		<span className="truncate text-xs text-muted-foreground leading-tight">
																			{m.email}
																			<span className="text-border px-1">
																				/
																			</span>
																			{m.role}
																		</span>
																	</div>
																</SelectItem>
															))}
														</SelectContent>
													</Select>
												</div>
											) : (
												<span>
													{PREDEFINED_CHANNEL_OPTIONS.find(
														(p) => p.value === field.predefined,
													)?.label ?? field.predefined}
												</span>
											)}
										</div>
										<Button
											type="button"
											variant="ghost"
											size="icon"
											onClick={() => remove(index)}
										>
											<IconTrash className="h-4 w-4" />
										</Button>
									</div>
								))}
							</div>
							<Select
								onValueChange={(v) => {
									if (typeof v === "string") addTarget(v);
								}}
							>
								<SelectTrigger>
									<div className="flex items-center gap-2 text-muted-foreground">
										<IconPlus className="h-4 w-4" />
										<span>Add target</span>
									</div>
								</SelectTrigger>
								<SelectContent>
									<SelectGroup>
										<SelectLabel>Role-based</SelectLabel>
										{PREDEFINED_CHANNEL_OPTIONS.map((p) => (
											<SelectItem key={p.value} value={`predefined:${p.value}`}>
												{p.label}
											</SelectItem>
										))}
									</SelectGroup>
									{verifiedChannels.length > 0 && (
										<SelectGroup>
											<SelectLabel>Saved channels</SelectLabel>
											{verifiedChannels.map((ch) => (
												<SelectItem key={ch.id} value={`channel:${ch.id}`}>
													<span>{ch.name}</span>
													<Badge variant="outline" className="ml-2 text-xs">
														{ch.type}
													</Badge>
												</SelectItem>
											))}
										</SelectGroup>
									)}
								</SelectContent>
							</Select>
							{form.formState.errors.channels && (
								<p className="text-sm text-destructive">
									{form.formState.errors.channels.message}
								</p>
							)}
						</div>

						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => onOpenChange(false)}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={isLoading}>
								{isLoading
									? isEdit
										? "Saving…"
										: "Creating…"
									: isEdit
										? "Save changes"
										: "Create rule"}
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
}
